'use client'

import InputField from '@trustoo/components/input/InputField'
import {
    ChangePasswordFields,
    ChangePasswordServerError,
    getChangePasswordErrors,
    getChangePasswordServerError,
    PasswordFormError,
} from '@trustoo/components/login/funcs/passwordForm'
import { usePasswordScore } from '@trustoo/components/login/hooks/usePasswordScore'
import LoginBox from '@trustoo/components/login/LoginBox'
import LoginButton from '@trustoo/components/login/LoginButton'
import PasswordStrength, { PasswordStrengthCopy } from '@trustoo/components/login/PasswordStrength'
import PasswordVisibilityToggle from '@trustoo/components/login/PasswordVisibilityToggle'
import TokenValidationError, {
    TokenValidationErrorCopy,
} from '@trustoo/components/login/TokenValidationError'
import AnimatedPages from '@trustoo/components/ui/AnimatedPages'
import { useEnterKeyPress } from '@trustoo/hooks/useEnterKeyPress'
import { ChangePasswordResponse } from '@trustoo/types/api/controllers/auth'
import { CheckCircle } from '@untitledui/icons'
import { ChangeEvent, useEffect, useState } from 'react'

export interface ChangePasswordCopy {
    title: string
    currentPassword: string
    newPassword: string
    repeatPassword: string
    submit: string
    success: string
    login: string
    errors: Record<PasswordFormError | ChangePasswordServerError, string>
    strength: PasswordStrengthCopy
    tokenError: TokenValidationErrorCopy
}

export interface ChangePasswordRequest {
    password: string
    password_confirm: string
    current_password?: string
}

export interface ChangePasswordProps {
    /** From a password reset link. Without one, the current password is asked for instead. */
    token?: string
    /** Where the reset link was sent, to request a new link for when it can't be used. */
    email?: string
    logoUrl: string
    copy: ChangePasswordCopy
    /** Rejects with `{ error }` when the reset link can't be used. */
    validateToken: (token: string) => Promise<unknown>
    /** Rejects with `{ error }` when the backend refuses the new password. */
    changePassword: (request: ChangePasswordRequest) => Promise<ChangePasswordResponse>
    getLoginHref: (email: string) => string
    /**
     * Where to request a new reset link when one can't be used. Without it, the copy has to say how
     * to get a new link, e.g. from an admin.
     */
    getResetPasswordHref?: (email?: string) => string
    className?: string
}

const getErrorCode = (response: unknown) => (response as { error?: string } | undefined)?.error

/**
 * Sets a new password, either from a reset link or by confirming the current one. Ends with a link
 * to log in, or with a way to request a new link when the reset link can't be used.
 */
const ChangePassword = ({
    token,
    email,
    logoUrl,
    copy,
    validateToken,
    changePassword,
    getLoginHref,
    getResetPasswordHref,
    className,
}: ChangePasswordProps) => {
    const askCurrentPassword = !token
    const [fields, setFields] = useState<ChangePasswordFields>({
        currentPassword: '',
        password: '',
        passwordConfirm: '',
    })
    const [errors, setErrors] = useState<Partial<Record<keyof ChangePasswordFields, string>>>({})
    const [visibleFields, setVisibleFields] = useState<
        Partial<Record<keyof ChangePasswordFields, boolean>>
    >({})
    const [isLoading, setIsLoading] = useState(Boolean(token))
    const [tokenError, setTokenError] = useState<string | null>(null)
    const [changedEmail, setChangedEmail] = useState<string | null>(null)
    const score = usePasswordScore(fields.password)

    useEffect(() => {
        if (!token) {
            return
        }

        setIsLoading(true)
        validateToken(token).then(
            () => setIsLoading(false),
            (response: unknown) => {
                setIsLoading(false)
                setTokenError(getErrorCode(response) ?? 'invalid token')
            },
        )
    }, [token])

    const pageNumber = (() => {
        if (tokenError) return 3
        if (changedEmail !== null) return 2
        return 1
    })()

    const submit = () => {
        const fieldErrors = getChangePasswordErrors(fields, askCurrentPassword, score)
        setErrors(
            Object.fromEntries(
                Object.entries(fieldErrors).map(([field, error]) => [field, copy.errors[error]]),
            ),
        )

        if (Object.keys(fieldErrors).length > 0) {
            return
        }

        setIsLoading(true)
        changePassword({
            password: fields.password,
            password_confirm: fields.passwordConfirm,
            ...(askCurrentPassword && { current_password: fields.currentPassword }),
        }).then(
            (response) => {
                setIsLoading(false)
                setChangedEmail(response.email ?? '')
            },
            (response: unknown) => {
                setIsLoading(false)
                const { field, error } = getChangePasswordServerError(getErrorCode(response))
                setErrors({ [field]: copy.errors[error] })
            },
        )
    }

    useEnterKeyPress(submit, pageNumber === 1 && !isLoading)

    const onChange = (field: keyof ChangePasswordFields) => (e: ChangeEvent<HTMLInputElement>) => {
        setFields((previous) => ({ ...previous, [field]: e.target.value }))
        setErrors((previous) => ({ ...previous, [field]: undefined }))
    }

    const renderPasswordField = (
        field: keyof ChangePasswordFields,
        placeholder: string,
        withStrength = false,
    ) => {
        const isVisible = Boolean(visibleFields[field])

        return (
            <InputField
                name={field}
                type={isVisible ? 'text' : 'password'}
                value={fields[field]}
                onChange={onChange(field)}
                error={errors[field]}
                placeholder={placeholder}
                autoFocus={field === 'password'}
                after={
                    <div className="flex items-center gap-1.5">
                        <PasswordVisibilityToggle
                            isVisible={isVisible}
                            onToggle={() =>
                                setVisibleFields((previous) => ({
                                    ...previous,
                                    [field]: !isVisible,
                                }))
                            }
                        />
                        {withStrength && score !== null && (
                            <PasswordStrength score={score} copy={copy.strength} />
                        )}
                    </div>
                }
            />
        )
    }

    return (
        <LoginBox isLoading={isLoading} title={copy.title} logoUrl={logoUrl} className={className}>
            <AnimatedPages pageNumber={pageNumber}>
                <div className="flex flex-col gap-4 pt-4">
                    {askCurrentPassword &&
                        renderPasswordField('currentPassword', copy.currentPassword)}
                    {renderPasswordField('password', copy.newPassword, true)}
                    {renderPasswordField('passwordConfirm', copy.repeatPassword)}
                    <div className="flex justify-end">
                        <LoginButton onClick={submit} disabled={isLoading}>
                            {copy.submit}
                        </LoginButton>
                    </div>
                </div>
                <div className="flex flex-col gap-4 pt-4">
                    <p className="m-0 flex items-center gap-2 font-bold">
                        <CheckCircle className="size-5 shrink-0 text-[#12c33e]" />
                        {copy.success}
                    </p>
                    <a
                        href={getLoginHref(changedEmail ?? '')}
                        className="font-bold text-[#5788ff] underline hover:text-[#10185a]">
                        {copy.login}
                    </a>
                </div>
                <div className="pt-4">
                    {tokenError && (
                        <TokenValidationError
                            error={tokenError}
                            resetPasswordHref={getResetPasswordHref?.(email)}
                            copy={copy.tokenError}
                        />
                    )}
                </div>
            </AnimatedPages>
        </LoginBox>
    )
}

export default ChangePassword
